import type { MusicManifest, Track } from "../music.ts";

const esc = (s: string) =>
  s.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!),
  );

const fmtTime = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
};

const VOL_KEY = "lafiesta:music:vol";
const MUTE_KEY = "lafiesta:music:muted";
const DEFAULT_VOL = 0.85;

export interface MusicPlayer {
  // Begin/resume playback. Browsers block autoplay, so call this from a user
  // gesture (e.g. entering the party). A no-op if there are no tracks.
  start(): void;
  dispose(): void;
}

const readVol = (): number => {
  const v = Number(localStorage.getItem(VOL_KEY));
  return Number.isFinite(v) && v >= 0 && v <= 1 ? v : DEFAULT_VOL;
};
const readMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
};
const save = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch { /* private mode — ignore */ }
};

// Music player (top-right). Collapsed to a 🎵 button; clicking it opens a
// Spotify-style panel — now-playing, transport, volume, and the full track
// list — and starts playback immediately. AI-generated tracks are marked
// distinctly from human artists (violet "🤖 IA"), never framed as a person.
export function createMusicPlayer(manifest: MusicManifest): MusicPlayer {
  const tracks = manifest.tracks;
  if (!tracks.length) return { start() {}, dispose() {} };

  const audio = new Audio();
  audio.preload = "none";
  audio.volume = readVol();
  audio.muted = readMuted();
  let i = 0;

  const load = (idx: number, play: boolean) => {
    i = (idx + tracks.length) % tracks.length;
    audio.src = tracks[i].src;
    resetSeek(); // new song → seek bar back to the beginning
    render();
    if (play) audio.play().catch(() => {/* blocked until a gesture */});
  };
  audio.addEventListener("ended", () => load(i + 1, true));

  // ── UI ─────────────────────────────────────────────────────────────────
  const wrap = document.createElement("div");
  wrap.className = "fixed right-4 top-4 z-20 flex flex-col items-end gap-2";

  const toggle = document.createElement("button");
  toggle.className =
    "h-12 w-12 rounded-full border-2 border-black bg-white text-xl shadow-lg";
  toggle.textContent = "🎵";
  toggle.title = "Música";

  const panel = document.createElement("div");
  panel.className =
    "hidden w-72 flex-col gap-2 rounded-xl border-2 border-black bg-white/95 p-3 text-sm shadow-lg [&:not(.hidden)]:flex";

  const now = document.createElement("div");
  now.className = "leading-tight";

  // ── Seek bar (resets to 0:00 on every new song) ──────────────────────────
  const progRow = document.createElement("div");
  progRow.className =
    "flex items-center gap-2 text-[10px] tabular-nums opacity-60";
  const elapsed = document.createElement("span");
  elapsed.textContent = "0:00";
  const seek = document.createElement("input");
  seek.type = "range";
  seek.min = "0";
  seek.max = "1";
  seek.step = "0.001";
  seek.value = "0";
  seek.className = "flex-1 accent-black";
  const dur = document.createElement("span");
  dur.textContent = "0:00";
  progRow.append(elapsed, seek, dur);

  let seeking = false;
  const resetSeek = () => {
    seeking = false;
    seek.value = "0";
    elapsed.textContent = "0:00";
    dur.textContent = "0:00";
  };
  seek.addEventListener("input", () => {
    seeking = true;
    if (audio.duration) {
      elapsed.textContent = fmtTime(Number(seek.value) * audio.duration);
    }
  });
  seek.addEventListener("change", () => {
    if (audio.duration) audio.currentTime = Number(seek.value) * audio.duration;
    seeking = false;
  });
  audio.addEventListener("timeupdate", () => {
    if (seeking) return;
    const d = audio.duration;
    seek.value = d ? String(audio.currentTime / d) : "0";
    elapsed.textContent = fmtTime(audio.currentTime);
  });
  audio.addEventListener(
    "loadedmetadata",
    () => (dur.textContent = fmtTime(audio.duration)),
  );

  const row = document.createElement("div");
  row.className = "flex items-center gap-2";
  const mkBtn = (label: string, title: string, on: () => void) => {
    const b = document.createElement("button");
    b.className =
      "rounded-lg border-2 border-black px-2 py-1 text-base font-bold";
    b.textContent = label;
    b.title = title;
    b.addEventListener("click", on);
    return b;
  };
  const prev = mkBtn("⏮", "Anterior", () => load(i - 1, true));
  const playBtn = mkBtn("▶︎", "Reproducir", () => {
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  });
  const next = mkBtn("⏭", "Siguiente", () => load(i + 1, true));
  const muteBtn = mkBtn("🔊", "Silenciar", () => {
    audio.muted = !audio.muted;
    save(MUTE_KEY, audio.muted ? "1" : "0");
    render();
  });
  row.append(prev, playBtn, next, muteBtn);

  const vol = document.createElement("input");
  vol.type = "range";
  vol.min = "0";
  vol.max = "1";
  vol.step = "0.05";
  vol.value = String(audio.volume);
  vol.className = "w-full accent-black";
  vol.addEventListener("input", () => {
    audio.volume = Number(vol.value);
    if (audio.muted && Number(vol.value) > 0) {
      audio.muted = false;
      save(MUTE_KEY, "0");
    }
    save(VOL_KEY, vol.value);
    render();
  });

  const credit = document.createElement("p");
  credit.className = "text-[10px] leading-snug opacity-50";

  // ── Track list (Spotify-style): every song, click to play ────────────────
  const listLabel = document.createElement("div");
  listLabel.className = "mt-1 text-[10px] font-bold uppercase opacity-40";
  listLabel.textContent = `Canciones (${tracks.length})`;

  const list = document.createElement("ul");
  list.className =
    "flex max-h-52 flex-col gap-0.5 overflow-auto border-t border-black/10 pt-1";
  const rowEls: HTMLButtonElement[] = [];
  tracks.forEach((t, idx) => {
    const li = document.createElement("li");
    const b = document.createElement("button");
    b.className =
      "flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left hover:bg-neutral-100";
    const who = t.ai
      ? `<span class="text-violet-700">🤖 IA</span>`
      : `<span class="opacity-60">${esc(t.artist)}</span>`;
    b.innerHTML =
      `<span class="num w-4 shrink-0 text-center text-xs opacity-50">${
        idx + 1
      }</span>` +
      `<span class="min-w-0 flex-1 truncate"><b>${
        esc(t.title)
      }</b> · ${who}</span>`;
    b.addEventListener("click", () => load(idx, true));
    li.appendChild(b);
    list.appendChild(li);
    rowEls.push(b);
  });

  panel.append(now, progRow, row, vol, credit, listLabel, list);
  wrap.append(toggle, panel);

  // Clicking the icon opens/closes the panel — and starts playback on open if
  // nothing is playing yet (this click is the user gesture browsers require).
  toggle.addEventListener("click", () => {
    const opening = panel.classList.contains("hidden");
    panel.classList.toggle("hidden");
    if (opening && audio.paused) audio.play().catch(() => {});
  });

  function render() {
    const t: Track = tracks[i];
    // Artist line: a human artist is named (linked when we have a URL); an AI
    // track gets a distinct violet "🤖 IA" pill, never framed as a person.
    const who = t.ai
      ? `<span class="inline-flex items-center gap-1 rounded-full border border-violet-400 bg-violet-100 px-2 py-0.5 text-[11px] font-bold text-violet-700">🤖 Generada con IA</span>`
      : t.url
      ? `<a href="${
        esc(t.url)
      }" target="_blank" rel="noopener" class="opacity-70 underline">${
        esc(t.artist)
      }</a>`
      : `<span class="opacity-70">${esc(t.artist)}</span>`;
    now.innerHTML = `<b>${esc(t.title)}</b><br>${who}`;
    // Violet accent (button ring + panel border) while an AI track plays, so
    // the AI/real distinction reads even at a glance.
    toggle.classList.toggle("ring-2", t.ai);
    toggle.classList.toggle("ring-violet-500", t.ai);
    panel.classList.toggle("border-violet-500", t.ai);
    panel.classList.toggle("border-black", !t.ai);
    playBtn.textContent = audio.paused ? "▶︎" : "⏸";
    playBtn.title = audio.paused ? "Reproducir" : "Pausar";
    muteBtn.textContent = audio.muted ? "🔇" : "🔊";
    vol.value = String(audio.volume);
    // AI tracks credit the tool + show the prompt; human tracks need no credit.
    if (t.ai) {
      const tool = t.artist && t.artist !== "IA" ? ` · ${esc(t.artist)}` : "";
      credit.textContent =
        (t.prompt ? `“${t.prompt}”` : "Música generada por IA") + tool;
      credit.classList.remove("hidden");
    } else {
      credit.textContent = "";
      credit.classList.add("hidden");
    }
    // Highlight the current row and show ▶/⏸ in its number slot.
    rowEls.forEach((b, idx) => {
      const cur = idx === i;
      b.classList.toggle("bg-neutral-200", cur);
      b.classList.toggle("font-bold", cur);
      const num = b.querySelector(".num") as HTMLElement;
      num.textContent = cur ? (audio.paused ? "⏸" : "▶") : String(idx + 1);
    });
  }
  audio.addEventListener("play", render);
  audio.addEventListener("pause", render);

  load(0, false);
  document.body.appendChild(wrap);

  return {
    start() {
      audio.play().catch(() => {/* still blocked — user can press ▶︎ */});
    },
    dispose() {
      audio.pause();
      audio.src = "";
      wrap.remove();
    },
  };
}
